import React, { useState, useEffect } from 'react';
import { useNotification } from '../../../context/NotificationContext';
import { adminAPI } from '../../../utils/api';

const SupportTickets = () => {
    const { showSuccess, showError } = useNotification();
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all'); // all, open, resolved
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [response, setResponse] = useState('');

    const fetchTickets = React.useCallback(async () => {
        try {
            setLoading(true);
            const res = await adminAPI.getSupportTickets(filter !== 'all' ? filter : null);
            if (res.data?.success) {
                setTickets(res.data.data || []);
            }
        } catch (error) {
            showError('Failed to fetch support tickets');
        } finally {
            setLoading(false);
        }
    }, [filter, showError]);

    useEffect(() => {
        fetchTickets();
    }, [fetchTickets]);



    const handleStatusUpdate = async (ticketId, newStatus) => {
        try {
            const res = await adminAPI.updateTicket(ticketId, { status: newStatus });
            if (res.data?.success) {
                showSuccess(`Ticket marked as ${newStatus}`);
                fetchTickets();
                if (selectedTicket?._id === ticketId) {
                    setSelectedTicket(prev => ({ ...prev, status: newStatus }));
                }
            }
        } catch (error) {
            showError('Failed to update ticket status');
        }
    };

    const handleResponseSubmit = async (e) => {
        e.preventDefault();
        if (!response.trim()) return;

        try {
            const res = await adminAPI.updateTicket(selectedTicket._id, {
                admin_response: response,
                status: 'resolved' // Auto-resolve on response
            });

            if (res.data?.success) {
                showSuccess('Response sent and ticket resolved');
                setResponse('');
                setSelectedTicket(null);
                fetchTickets();
            }
        } catch (error) {
            showError('Failed to send response');
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'high': return 'text-red-400';
            case 'medium': return 'text-yellow-400';
            case 'low': return 'text-green-400';
            default: return 'text-gray-400';
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'open': return 'bg-blue-900/50 text-blue-300 border-blue-700';
            case 'in_progress': return 'bg-yellow-900/50 text-yellow-300 border-yellow-700';
            case 'resolved': return 'bg-green-900/50 text-green-300 border-green-700';
            case 'closed': return 'bg-gray-700 text-gray-400 border-gray-600';
            default: return 'bg-gray-700 text-gray-400';
        }
    };

    return (
        <div className="flex h-[calc(100vh-64px)] overflow-hidden">
            {/* Ticket List Sidebar */}
            <div className="w-1/3 border-r border-gray-700 flex flex-col bg-gray-900">
                <div className="p-4 border-b border-gray-700">
                    <h2 className="text-xl font-bold text-gold mb-4">Support Tickets</h2>
                    <div className="flex space-x-2">
                        {['all', 'open', 'resolved'].map(f => (
                            <button
                                key={f}
                                onClick={() => { setFilter(f); fetchTickets(); }}
                                className={`px-3 py-1 rounded-full text-xs font-medium capitalize border ${filter === f
                                    ? 'bg-gold text-dark border-gold'
                                    : 'bg-transparent text-gray-400 border-gray-600 hover:border-gray-400'
                                    }`}
                            >
                                {f}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto">
                    {loading ? (
                        <div className="p-4 text-center text-gray-400">Loading...</div>
                    ) : tickets.length === 0 ? (
                        <div className="p-8 text-center text-gray-500">No tickets found</div>
                    ) : (
                        tickets.map(ticket => (
                            <div
                                key={ticket._id}
                                onClick={() => setSelectedTicket(ticket)}
                                className={`p-4 border-b border-gray-800 cursor-pointer hover:bg-gray-800 transition-colors ${selectedTicket?._id === ticket._id ? 'bg-gray-800 border-l-4 border-l-gold' : ''
                                    }`}
                            >
                                <div className="flex justify-between items-start mb-1">
                                    <h3 className="font-semibold text-light truncate pr-2">{ticket.subject}</h3>
                                    <span className={`text-xs px-2 py-0.5 rounded border ${getStatusBadge(ticket.status)} capitalize`}>
                                        {ticket.status.replace('_', ' ')}
                                    </span>
                                </div>
                                <div className="flex justify-between text-xs text-gray-400 mb-2">
                                    <span>{ticket.user_id?.name || 'Unknown User'}</span>
                                    <span>{new Date(ticket.createdAt).toLocaleDateString()}</span>
                                </div>
                                <p className="text-sm text-gray-500 line-clamp-2">{ticket.description}</p>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Ticket Detail View */}
            <div className="flex-1 flex flex-col bg-dark">
                {selectedTicket ? (
                    <>
                        <div className="p-6 border-b border-gray-700 bg-gray-900/50">
                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <h1 className="text-2xl font-bold text-white mb-2">{selectedTicket.subject}</h1>
                                    <div className="flex items-center space-x-4 text-sm text-gray-400">
                                        <span>From: <strong className="text-light">{selectedTicket.user_id?.name}</strong> ({selectedTicket.user_id?.email})</span>
                                        <span>•</span>
                                        <span className={`capitalize ${getPriorityColor(selectedTicket.priority)}`}>{selectedTicket.priority} Priority</span>
                                    </div>
                                </div>
                                <div className="flex space-x-2">
                                    {selectedTicket.status !== 'resolved' && (
                                        <button
                                            onClick={() => handleStatusUpdate(selectedTicket._id, 'resolved')}
                                            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition"
                                        >
                                            Mark Resolved
                                        </button>
                                    )}
                                    {selectedTicket.status !== 'closed' && (
                                        <button
                                            onClick={() => handleStatusUpdate(selectedTicket._id, 'closed')}
                                            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg text-sm font-medium transition"
                                        >
                                            Close Ticket
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6 space-y-6">
                            <div className="bg-gray-800/50 rounded-xl p-6">
                                <h4 className="text-sm font-medium text-gray-400 mb-2">Description</h4>
                                <p className="text-gray-200 whitespace-pre-wrap leading-relaxed">{selectedTicket.description}</p>
                            </div>

                            {selectedTicket.admin_response && (
                                <div className="bg-blue-900/20 rounded-xl p-6 border border-blue-800/50 ml-8">
                                    <div className="flex items-center space-x-2 mb-2">
                                        <div className="w-6 h-6 bg-gold rounded-full flex items-center justify-center">
                                            <span className="text-dark font-bold text-xs">A</span>
                                        </div>
                                        <h4 className="text-sm font-bold text-gold">Admin Response</h4>
                                    </div>
                                    <p className="text-gray-200 whitespace-pre-wrap leading-relaxed">{selectedTicket.admin_response}</p>
                                </div>
                            )}
                        </div>

                        {selectedTicket.status !== 'closed' && selectedTicket.status !== 'resolved' && (
                            <div className="p-4 border-t border-gray-700 bg-gray-900">
                                <form onSubmit={handleResponseSubmit}>
                                    <label className="block text-sm font-medium text-gray-400 mb-2">Reply to User</label>
                                    <textarea
                                        value={response}
                                        onChange={(e) => setResponse(e.target.value)}
                                        className="w-full bg-gray-800 border border-gray-600 rounded-lg p-3 text-light focus:ring-2 focus:ring-gold focus:border-transparent outline-none resize-none h-32"
                                        placeholder="Type your response here..."
                                    />
                                    <div className="flex justify-end mt-3">
                                        <button
                                            type="submit"
                                            disabled={!response.trim()}
                                            className="px-6 py-2 bg-gold text-dark font-bold rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Send & Resolve
                                        </button>
                                    </div>
                                </form>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="flex-1 flex flex-col items-center justify-center text-gray-500">
                        <svg className="w-16 h-16 mb-4 opacity-20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                        </svg>
                        <p>Select a ticket to view details</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SupportTickets;
